import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { ArrowDownRight, ArrowUpRight, Calendar, DollarSign, Minus, UserX, XCircle } from "react-feather";
import { sendGetRequest } from "../../../../actions/app-actions";
import { getAdminBusinessId } from "../../../../utils/authHelper";
import { formatMoney, formatNumber } from "./chartTheme";

const PERIODS = [
  { key: "7", label: "7D", days: 7, text: "Last 7 days" },
  { key: "30", label: "30D", days: 30, text: "Last 30 days" },
  { key: "90", label: "90D", days: 90, text: "Last 90 days" },
  { key: "365", label: "12M", days: 365, text: "Last 12 months" },
];

const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

/** Change vs. previous period; `inverse` marks metrics where an increase is bad. */
const change = (current, previous, inverse = false) => {
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (!prev && !cur) return { text: "0%", dir: "flat", tone: "neutral" };
  if (!prev) return { text: "New", dir: "up", tone: inverse ? "negative" : "positive" };
  const diff = ((cur - prev) / prev) * 100;
  const abs = Math.abs(diff);
  const dir = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
  const good = inverse ? dir === "down" : dir === "up";
  return {
    text: `${abs >= 10 ? Math.round(abs) : abs.toFixed(1)}%`,
    dir,
    tone: dir === "flat" ? "neutral" : good ? "positive" : "negative",
  };
};

const DeltaIcon = ({ dir }) =>
  dir === "up" ? <ArrowUpRight size={13} /> : dir === "down" ? <ArrowDownRight size={13} /> : <Minus size={13} />;

/**
 * KPI summary for the analytics dashboard. Uses admin/crm-booking-sales with a
 * date range, which also returns the previous same-length period for comparison.
 */
const DashboardStats = (props) => {
  const [periodKey, setPeriodKey] = useState("30");
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("loading");
  const latest = useRef(0);

  const period = PERIODS.find((p) => p.key === periodKey) || PERIODS[1];

  useEffect(() => {
    const id = ++latest.current;
    const end = moment();
    const start = moment().subtract(period.days - 1, "days");
    setStatus((s) => (s === "ready" || s === "refreshing" ? "refreshing" : "loading"));
    props.sendGetRequest({
      url: `/admin/crm-booking-sales/${getAdminBusinessId()}?start_date=${start.format("YYYY-MM-DD")}&end_date=${end.format("YYYY-MM-DD")}`,
      onSuccess: (data) => {
        if (id !== latest.current) return;
        setStats(data || {});
        setStatus("ready");
      },
      onError: () => {
        if (id !== latest.current) return;
        setStats(null);
        setStatus("error");
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodKey]);

  const s = stats || {};
  const p = s.previous || {};
  const booked = Number(s.event_booked) || 0;
  const cancelled = Number(s.event_cancelled) || 0;
  const noShow = Number(s.no_show) || 0;
  const volume = booked + cancelled + noShow;
  const revenue = Number(s.total_revenue) || 0;

  const cards = [
    {
      key: "revenue",
      label: "Net revenue",
      value: formatMoney(revenue),
      delta: change(revenue, p.total_revenue),
      caption: booked ? `${formatMoney(revenue / booked)} average per booking` : "No paid bookings yet",
      icon: DollarSign,
      tone: "brand",
    },
    {
      key: "bookings",
      label: "Bookings",
      value: formatNumber(booked),
      delta: change(booked, p.event_booked),
      caption: "Confirmed, pending & completed",
      icon: Calendar,
      tone: "success",
    },
    {
      key: "cancelled",
      label: "Cancellations",
      value: formatNumber(cancelled),
      delta: change(cancelled, p.event_cancelled, true),
      caption: `${pct(cancelled, volume)}% cancellation rate · ${formatMoney(s.revenue_lost_from_cancellation)} lost`,
      icon: XCircle,
      tone: "danger",
    },
    {
      key: "noshow",
      label: "No-shows",
      value: formatNumber(noShow),
      delta: change(noShow, p.no_show, true),
      caption: `${pct(noShow, volume)}% no-show rate · ${formatMoney(s.revenue_lost_from_no_show)} lost`,
      icon: UserX,
      tone: "warning",
    },
  ];

  const loading = status === "loading";

  return (
    <section className="dash-section">
      <div className="dash-section__head">
        <div>
          <h2 className="dash-section__title">Overview</h2>
          <p className="dash-section__hint">
            {period.text} by appointment date, compared with the prior {period.days === 365 ? "12 months" : `${period.days} days`}
          </p>
        </div>
        <div className="dash-segmented" role="group" aria-label="Reporting period">
          {PERIODS.map((item) => (
            <button
              type="button"
              key={item.key}
              className={item.key === periodKey ? "active" : ""}
              aria-pressed={item.key === periodKey}
              onClick={() => setPeriodKey(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`dash-kpis ${status === "refreshing" ? "is-refreshing" : ""}`}>
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div className={`dash-kpi dash-kpi--${c.tone}`} key={c.key}>
              <div className="dash-kpi__head">
                <span className="dash-kpi__label">{c.label}</span>
                <span className="dash-kpi__icon">
                  <Icon size={16} />
                </span>
              </div>
              {loading ? (
                <div className="dash-kpi__skeleton">
                  <span />
                  <span />
                </div>
              ) : status === "error" ? (
                <div className="dash-kpi__error">Unavailable</div>
              ) : (
                <>
                  <div className="dash-kpi__value">{c.value}</div>
                  <div className="dash-kpi__foot">
                    <span className={`dash-delta dash-delta--${c.delta.tone}`}>
                      <DeltaIcon dir={c.delta.dir} />
                      {c.delta.text}
                    </span>
                    <span className="dash-kpi__compare">vs prior period</span>
                  </div>
                  <div className="dash-kpi__caption">{c.caption}</div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default connect(null, { sendGetRequest })(DashboardStats);
